import styled from 'styled-components';
import { BookingApi, Plain } from '@agendor/shared';
import { vars, Button, Input, formatCpfCnpj, gtagEvent } from '@agendor/ui';
import { TimeSlot, BusinessInfo } from '../booking-service';
import { ButtonGroup, ConfirmSection, ConfirmLabel, ConfirmValue, NotesTextArea, ErrorText } from '../shared-styles';
import { formatPrice, formatTime, formatSelectedDate } from '../formatting';
import { MONTH_NAMES, getCalendarDays } from '../calendar-utils';
import BookingService from '../booking-service';
import IntakeSection from './IntakeSection';

const PAYMENT_METHOD_LABELS: Record<NonNullable<BusinessInfo['payment']>['method'], string> = {
    pix: 'Pix',
    card: 'Cartão de crédito ou débito',
    boleto: 'Boleto ou Pix',
    any: 'Pix, cartão ou boleto',
};

export type WizardScreen = 'worker' | 'services' | 'schedule' | 'confirm' | 'intake' | 'success';

const ListCard = styled.button<{ selected?: boolean }>`
    width: 100%;
    padding: 16px;
    border: 1px solid ${(props) => (props.selected ? vars.accentColor : vars.dividerColor)};
    border-radius: 8px;
    margin-bottom: 12px;
    cursor: pointer;
    background-color: ${(props) => (props.selected ? vars.primaryColorLight : '#fff')};
    text-align: left;
    font: inherit;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    -webkit-tap-highlight-color: transparent;

    @media (max-width: 480px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
    }
`;

const ListCardInfo = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const ListCardName = styled.div`
    font-weight: 600;
    font-size: 1rem;
    color: ${vars.foregroundColor};
`;

const ListCardDescription = styled.div`
    font-size: 0.9rem;
    color: #888;
`;

const ListCardDetails = styled.div`
    display: flex;
    align-items: center;
    gap: 20px;
    flex-shrink: 0;

    @media (max-width: 480px) {
        width: 100%;
        justify-content: space-between;
    }
`;

const DetailItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    text-align: right;

    @media (max-width: 480px) {
        text-align: left;
    }
`;

const DetailLabel = styled.span`
    font-size: 0.75rem;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const DetailValue = styled.span`
    font-size: 0.95rem;
    color: ${vars.foregroundColor};
    font-weight: 600;
`;

const WorkerCard = styled(ListCard)``;

const WorkerInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
`;

const WorkerAvatar = styled.div`
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background-color: ${vars.primaryColor};
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    color: ${vars.accentColor};
    font-size: 1.1rem;
    flex-shrink: 0;
`;

const WorkerDetails = styled.div`
    flex: 1;
    min-width: 0;
`;

const WorkerName = styled.div`
    font-weight: 600;
    color: ${vars.foregroundColor};
`;

const WorkerTitle = styled.div`
    font-size: 0.85rem;
    color: #888;
`;

const CalendarNav = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
`;

const CalendarMonth = styled.div`
    font-weight: 600;
    font-size: 1rem;
`;

const CalendarNavButton = styled.button`
    width: 36px;
    height: 36px;
    padding: 0;
    background: transparent;
    border: 1px solid ${vars.foregroundColor};
    border-radius: 6px;
    cursor: pointer;
    font: inherit;
    font-size: 1rem;
    color: ${vars.foregroundColor};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    -webkit-tap-highlight-color: transparent;

    &:hover {
        background-color: ${vars.primaryColorLight};
    }

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }
`;

const DateGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 4px;
    margin-bottom: 20px;
`;

const DateHeader = styled.div`
    text-align: center;
    font-size: 0.75rem;
    color: #888;
    padding: 4px;
    font-weight: 500;
`;

const DateCell = styled.button<{ selected?: boolean; disabled?: boolean }>`
    aspect-ratio: 1;
    border: 1px solid ${(props) => (props.selected ? vars.dividerColor : 'transparent')};
    border-radius: 6px;
    font: inherit;
    font-size: 0.85rem;
    cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
    background-color: ${(props) => (props.selected ? vars.primaryColor : 'transparent')};
    color: ${(props) => (props.selected ? vars.foregroundColor : props.disabled ? '#ccc' : vars.foregroundColor)};
    font-weight: ${(props) => (props.selected ? '600' : '400')};
    outline: none;
    -webkit-tap-highlight-color: transparent;

    &:hover:not(:disabled) {
        background-color: ${(props) => (props.selected ? vars.primaryColor : vars.backgroundColorDark)};
    }
`;

const TimeGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
`;

const TimeSlotButton = styled.button<{ selected?: boolean }>`
    padding: 12px;
    border: 1px solid ${(props) => (props.selected ? vars.accentColor : vars.dividerColor)};
    border-radius: 6px;
    background-color: ${(props) => (props.selected ? vars.primaryColorLight : 'transparent')};
    color: ${vars.foregroundColor};
    cursor: pointer;
    font: inherit;
    font-size: 0.9rem;
    outline: none;
    -webkit-tap-highlight-color: transparent;

    &:hover {
        border-color: ${vars.accentColor};
    }
`;

const StepIndicator = styled.div`
    display: flex;
    justify-content: center;
    gap: 8px;
    margin-bottom: 20px;
`;

const StepDot = styled.div<{ active?: boolean; completed?: boolean }>`
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: ${(props) => (props.active || props.completed ? vars.primaryColor : vars.dividerColor)};
    border: 1px solid ${(props) => (props.active || props.completed ? vars.dividerColor : 'transparent')};
`;

const SectionTitle = styled.h3`
    font-size: 0.95rem;
    font-weight: 600;
    margin: 0 0 12px 0;
`;

const SuccessIcon = styled.div`
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background-color: ${vars.successTintSoft};
    border: 1px solid ${vars.dividerColor};
    color: ${vars.accentColor};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    margin: 0 auto 20px;
`;

const NotificationHint = styled.p`
    font-size: 0.8rem;
    color: #888;
    text-align: center;
    margin-top: 8px;
`;

type Props = {
    screen: WizardScreen;
    services: BusinessInfo['services'];
    workers: BusinessInfo['workers'];
    payment: BusinessInfo['payment'];
    reminders: BusinessInfo['reminders'];
    selectedServices: number[];
    selectedWorker: number | null;
    selectedDate: Date | null;
    selectedTime: TimeSlot | null;
    calendarMonth: Plain.Month;
    availableSlots: TimeSlot[];
    isLoadingSlots: boolean;
    slotsError: boolean;
    bookingNotes: string;
    cpfCnpj: string;
    cpfCnpjError: string | null;
    isConfirming: boolean;
    paymentInvoiceUrl: string | null;
    today: Date;
    maxDate: Date | null;
    onToggleService: (serviceId: number) => void;
    onSelectWorker: (workerId: number | null) => void;
    onSelectDate: (day: number) => void;
    onSelectTime: (slot: TimeSlot) => void;
    onPrevMonth: () => void;
    onNextMonth: () => void;
    onSetBookingNotes: (notes: string) => void;
    onSetCpfCnpj: (value: string) => void;
    onConfirmBooking: () => void;
    onViewBookings: () => void;
    onLoadSlots: (date: Date) => void;
    onNavigate: (screen: string) => void;
    onBackFromServices: () => void;
    onContinueFromServices: () => void;
    onContinueFromWorker: () => void;
    /** Pending intakes pre-resolved for the booking being created. */
    wizardIntakes: BookingApi.Intake[];
    lastBookingId: string | null;
    businessSlug: string;
    bookingService: BookingService;
};

export default function NewBookingSection({
    screen,
    services,
    workers,
    payment,
    reminders,
    selectedServices,
    selectedWorker,
    selectedDate,
    selectedTime,
    calendarMonth,
    availableSlots,
    isLoadingSlots,
    slotsError,
    bookingNotes,
    cpfCnpj,
    cpfCnpjError,
    isConfirming,
    paymentInvoiceUrl,
    today,
    maxDate,
    onToggleService,
    onSelectWorker,
    onSelectDate,
    onSelectTime,
    onPrevMonth,
    onNextMonth,
    onSetBookingNotes,
    onSetCpfCnpj,
    onConfirmBooking,
    onViewBookings,
    onLoadSlots,
    onNavigate,
    onBackFromServices,
    onContinueFromServices,
    onContinueFromWorker,
    wizardIntakes,
    lastBookingId,
    businessSlug,
    bookingService,
}: Props) {
    const getSteps = (): WizardScreen[] => {
        return workers.length === 1
            ? ['services', 'schedule', 'confirm']
            : ['worker', 'services', 'schedule', 'confirm'];
    };

    const getStepIndex = () => {
        return getSteps().indexOf(screen);
    };

    const getAvailableServices = () => {
        if (selectedWorker === null) {
            return services.filter((service) => workers.some((worker) => worker.servicesIds.includes(service.id)));
        }
        const worker = workers.find((w) => w.id === selectedWorker);
        if (!worker) return services;
        return services.filter((s) => worker.servicesIds.includes(s.id));
    };

    const getWorkersForSelectedServices = () => {
        if (selectedServices.length === 0) return workers;
        return workers.filter((worker) =>
            selectedServices.every((serviceId) => worker.servicesIds.includes(serviceId)),
        );
    };

    const isDateDisabled = (day: number) => {
        const date = new Date(calendarMonth.year, calendarMonth.month, day);
        return date < today || (maxDate !== null && date > maxDate);
    };

    const isDateSelected = (day: number) => {
        if (!selectedDate) return false;
        return (
            selectedDate.getFullYear() === calendarMonth.year &&
            selectedDate.getMonth() === calendarMonth.month &&
            selectedDate.getDate() === day
        );
    };

    const steps = getSteps();
    const stepIndex = getStepIndex();

    const renderStepIndicator = () => (
        <StepIndicator>
            {steps.map((_, i) => (
                <StepDot key={i} active={stepIndex === i} completed={stepIndex > i} />
            ))}
        </StepIndicator>
    );

    switch (screen) {
        case 'worker':
            return (
                <>
                    {renderStepIndicator()}
                    <h2 data-testid="step-worker">Escolha o profissional</h2>
                    <p>Selecione quem vai te atender.</p>
                    <WorkerCard
                        selected={selectedWorker === null}
                        onClick={() => {
                            gtagEvent('worker_select_click', { item_id: null });
                            onSelectWorker(null);
                        }}
                        data-testid="no-preference-option"
                    >
                        <WorkerInfo>
                            <WorkerAvatar>?</WorkerAvatar>
                            <WorkerDetails>
                                <WorkerName>Sem preferência</WorkerName>
                                <WorkerTitle>Qualquer profissional disponível</WorkerTitle>
                            </WorkerDetails>
                        </WorkerInfo>
                    </WorkerCard>
                    {workers.map((worker) => (
                        <WorkerCard
                            key={worker.id}
                            selected={selectedWorker === worker.id}
                            onClick={() => {
                                gtagEvent('worker_select_click', { item_id: worker.id });
                                onSelectWorker(worker.id);
                            }}
                        >
                            <WorkerInfo>
                                <WorkerAvatar>{worker.name[0]}</WorkerAvatar>
                                <WorkerDetails>
                                    <WorkerName>{worker.name}</WorkerName>
                                    <WorkerTitle>{worker.title}</WorkerTitle>
                                </WorkerDetails>
                            </WorkerInfo>
                        </WorkerCard>
                    ))}
                    <ButtonGroup>
                        <Button
                            onClick={() => {
                                gtagEvent('worker_back_click');
                                onNavigate('bookings');
                            }}
                            data-testid="back-btn"
                        >
                            Voltar
                        </Button>
                        <Button
                            primary
                            onClick={() => {
                                gtagEvent('worker_continue_click');
                                onContinueFromWorker();
                            }}
                            data-testid="continue-btn"
                        >
                            Continuar
                        </Button>
                    </ButtonGroup>
                </>
            );

        case 'services': {
            const availableServices = getAvailableServices();
            const compatibleWorkers = selectedWorker === null ? getWorkersForSelectedServices() : [];
            const canProceed = selectedServices.length > 0 && (selectedWorker !== null || compatibleWorkers.length > 0);
            return (
                <>
                    {renderStepIndicator()}
                    <h2 data-testid="step-services">Escolha os serviços</h2>
                    <p>Selecione um ou mais serviços que deseja agendar.</p>
                    {payment && (
                        <div
                            style={{
                                marginBottom: 16,
                                padding: 12,
                                backgroundColor: vars.warningTintSoft,
                                borderRadius: 6,
                                border: `1px solid ${vars.dividerColor}`,
                                fontSize: '0.85rem',
                            }}
                        >
                            {payment.percentage === 100
                                ? 'Pagamento integral é necessário para confirmar o agendamento.'
                                : `Pagamento antecipado de ${payment.percentage}% é necessário para confirmar o agendamento. Valor mínimo de R$ 5,00.`}
                            {payment.timeoutMinutes !== null &&
                                ` O prazo para pagamento é de ${payment.timeoutMinutes} ${payment.timeoutMinutes === 1 ? 'minuto' : 'minutos'}.`}
                        </div>
                    )}
                    {availableServices.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: 24, color: '#888' }}>
                            Nenhum serviço disponível.
                        </div>
                    ) : (
                        availableServices.map((service) => (
                            <ListCard
                                key={service.id}
                                selected={selectedServices.includes(service.id)}
                                onClick={() => onToggleService(service.id)}
                            >
                                <ListCardInfo>
                                    <ListCardName>{service.name}</ListCardName>
                                    <ListCardDescription>{service.description}</ListCardDescription>
                                </ListCardInfo>
                                <ListCardDetails>
                                    <DetailItem>
                                        <DetailLabel>Duração</DetailLabel>
                                        <DetailValue>{service.durationInMinutes} min</DetailValue>
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>Valor</DetailLabel>
                                        <DetailValue className="price">{formatPrice(service.priceInCents)}</DetailValue>
                                    </DetailItem>
                                </ListCardDetails>
                            </ListCard>
                        ))
                    )}
                    {selectedWorker === null && availableServices.length > 0 && (
                        <div
                            style={{
                                marginTop: 16,
                                padding: 16,
                                backgroundColor: vars.backgroundColorDark,
                                borderRadius: 6,
                                border: `1px solid ${vars.dividerColor}`,
                            }}
                        >
                            <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: 8 }}>
                                Profissionais disponíveis:
                            </div>
                            {selectedServices.length === 0 ? (
                                <div style={{ fontSize: '0.9rem', color: '#888' }}>
                                    Selecione os serviços para ver os profissionais disponíveis.
                                </div>
                            ) : compatibleWorkers.length > 0 ? (
                                <div style={{ fontSize: '0.9rem' }}>
                                    {compatibleWorkers.map((w) => w.name).join(', ')}
                                </div>
                            ) : (
                                <div style={{ fontSize: '0.9rem', color: '#888' }}>
                                    Nenhum profissional realiza todos os serviços selecionados.
                                </div>
                            )}
                        </div>
                    )}
                    <ButtonGroup>
                        <Button
                            onClick={() => {
                                gtagEvent('services_back_click');
                                onBackFromServices();
                            }}
                            data-testid="back-btn"
                        >
                            Voltar
                        </Button>
                        <Button
                            primary
                            disabled={!canProceed}
                            onClick={() => {
                                gtagEvent('services_continue_click');
                                onContinueFromServices();
                            }}
                            data-testid="continue-btn"
                        >
                            Continuar
                        </Button>
                    </ButtonGroup>
                </>
            );
        }

        case 'schedule': {
            const isPrevMonthDisabled =
                calendarMonth.year === today.getFullYear() && calendarMonth.month <= today.getMonth();
            const nextMonthFirst = new Date(calendarMonth.year, calendarMonth.month + 1, 1);
            const isNextMonthDisabled = maxDate !== null && nextMonthFirst > maxDate;
            return (
                <>
                    {renderStepIndicator()}
                    <h2 data-testid="step-schedule">Data e horário</h2>
                    <CalendarNav>
                        <CalendarNavButton onClick={onPrevMonth} disabled={isPrevMonthDisabled}>
                            ←
                        </CalendarNavButton>
                        <CalendarMonth>
                            {MONTH_NAMES[calendarMonth.month]} {calendarMonth.year}
                        </CalendarMonth>
                        <CalendarNavButton onClick={onNextMonth} disabled={isNextMonthDisabled}>
                            →
                        </CalendarNavButton>
                    </CalendarNav>
                    <DateGrid>
                        {['D', 'S', 'T', 'Q', 'Q', 'S', 'S'].map((day, i) => (
                            <DateHeader key={i}>{day}</DateHeader>
                        ))}
                        {getCalendarDays(calendarMonth.year, calendarMonth.month).map((day, i) => (
                            <DateCell
                                key={i}
                                selected={day !== null && isDateSelected(day)}
                                disabled={day === null || isDateDisabled(day)}
                                onClick={() => day && !isDateDisabled(day) && onSelectDate(day)}
                            >
                                {day}
                            </DateCell>
                        ))}
                    </DateGrid>
                    {selectedDate && (
                        <>
                            <SectionTitle data-testid="available-slots">Horários disponíveis</SectionTitle>
                            {isLoadingSlots ? (
                                <div style={{ textAlign: 'center', padding: '20px 0', color: '#888' }}>
                                    Carregando horários...
                                </div>
                            ) : slotsError ? (
                                <div
                                    style={{
                                        textAlign: 'center',
                                        padding: '20px 0',
                                        color: '#888',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 12,
                                        alignItems: 'center',
                                    }}
                                >
                                    <div>Não foi possível carregar os horários.</div>
                                    <Button onClick={() => onLoadSlots(selectedDate)}>Tentar novamente</Button>
                                </div>
                            ) : availableSlots.length > 0 ? (
                                <TimeGrid>
                                    {availableSlots.map((slot) => {
                                        const timeStr = formatTime(slot);
                                        const isSelected =
                                            selectedTime?.hour === slot.hour && selectedTime?.minute === slot.minute;
                                        return (
                                            <TimeSlotButton
                                                key={timeStr}
                                                selected={isSelected}
                                                onClick={() => {
                                                    gtagEvent('schedule_time_click', { label: timeStr });
                                                    onSelectTime(slot);
                                                }}
                                            >
                                                {timeStr}
                                            </TimeSlotButton>
                                        );
                                    })}
                                </TimeGrid>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '20px 0', color: '#888' }}>
                                    Nenhum horário disponível nesta data.
                                </div>
                            )}
                        </>
                    )}
                    <ButtonGroup>
                        <Button
                            onClick={() => {
                                gtagEvent('schedule_back_click');
                                onNavigate('services');
                            }}
                            data-testid="back-btn"
                        >
                            Voltar
                        </Button>
                        <Button
                            primary
                            disabled={!selectedDate || !selectedTime}
                            onClick={() => {
                                gtagEvent('schedule_continue_click');
                                onNavigate('confirm');
                            }}
                            data-testid="continue-btn"
                        >
                            Continuar
                        </Button>
                    </ButtonGroup>
                </>
            );
        }

        case 'confirm': {
            const selectedServicesList = services.filter((s) => selectedServices.includes(s.id));
            const selectedWorkerObj = workers.find((w) => w.id === selectedWorker);
            const totalDuration = selectedServicesList.reduce((acc, s) => acc + s.durationInMinutes, 0);
            const totalPrice = selectedServicesList.reduce((acc, s) => acc + s.priceInCents, 0);

            return (
                <>
                    {renderStepIndicator()}
                    <h2 data-testid="step-confirm">Confirmar agendamento</h2>
                    <div style={{ marginTop: 16 }}>
                        <ConfirmSection>
                            <ConfirmLabel>{selectedServicesList.length > 1 ? 'Serviços' : 'Serviço'}</ConfirmLabel>
                            <ConfirmValue>{selectedServicesList.map((s) => s.name).join(', ')}</ConfirmValue>
                        </ConfirmSection>
                        <ConfirmSection>
                            <ConfirmLabel>Profissional</ConfirmLabel>
                            <ConfirmValue>{selectedWorkerObj?.name ?? 'Sem preferência'}</ConfirmValue>
                        </ConfirmSection>
                        <ConfirmSection>
                            <ConfirmLabel>Data</ConfirmLabel>
                            <ConfirmValue>{selectedDate ? formatSelectedDate(selectedDate) : ''}</ConfirmValue>
                        </ConfirmSection>
                        <ConfirmSection>
                            <ConfirmLabel>Horário</ConfirmLabel>
                            <ConfirmValue>{selectedTime ? formatTime(selectedTime) : ''}</ConfirmValue>
                        </ConfirmSection>
                        <ConfirmSection>
                            <ConfirmLabel>Duração</ConfirmLabel>
                            <ConfirmValue>{totalDuration} minutos</ConfirmValue>
                        </ConfirmSection>
                        {totalPrice > 0 && (
                            <ConfirmSection>
                                <ConfirmLabel>Valor total</ConfirmLabel>
                                <ConfirmValue>{formatPrice(totalPrice)}</ConfirmValue>
                            </ConfirmSection>
                        )}
                        {totalPrice > 0 && payment && (
                            <ConfirmSection>
                                <ConfirmLabel>
                                    {payment.percentage === 100 ? 'Pagamento' : 'Pagamento antecipado'}
                                </ConfirmLabel>
                                <ConfirmValue>
                                    {(() => {
                                        if (payment.percentage === 100) return formatPrice(totalPrice);
                                        const calculated = Math.round((totalPrice * payment.percentage) / 100);
                                        const amount = Math.max(500, calculated);
                                        return amount > calculated
                                            ? `${formatPrice(amount)} (valor mínimo)`
                                            : `${formatPrice(amount)} (${payment.percentage}%)`;
                                    })()}
                                </ConfirmValue>
                                <div
                                    data-testid="payment-method"
                                    style={{ fontSize: '0.8rem', color: '#888', marginTop: 4 }}
                                >
                                    Forma de pagamento: {PAYMENT_METHOD_LABELS[payment.method]}
                                </div>
                                {payment.timeoutMinutes !== null && (
                                    <div style={{ fontSize: '0.8rem', color: '#888', marginTop: 4 }}>
                                        Prazo: {payment.timeoutMinutes}{' '}
                                        {payment.timeoutMinutes === 1 ? 'minuto' : 'minutos'} após a confirmação
                                    </div>
                                )}
                            </ConfirmSection>
                        )}
                        <ConfirmSection>
                            <ConfirmLabel>Observações (opcional)</ConfirmLabel>
                            <NotesTextArea
                                value={bookingNotes}
                                onChange={(e) => onSetBookingNotes(e.target.value)}
                                placeholder="Alguma informação adicional?"
                                maxLength={300}
                            />
                        </ConfirmSection>
                        {payment?.needsCpf && (
                            <ConfirmSection>
                                <ConfirmLabel>CPF/CNPJ</ConfirmLabel>
                                <Input
                                    value={cpfCnpj}
                                    hasError={cpfCnpjError !== null}
                                    onChange={(e) => {
                                        onSetCpfCnpj(formatCpfCnpj(e.target.value));
                                    }}
                                    placeholder="000.000.000-00"
                                    inputMode="numeric"
                                    maxLength={18}
                                />
                                {cpfCnpjError && <ErrorText>{cpfCnpjError}</ErrorText>}
                            </ConfirmSection>
                        )}
                    </div>
                    <ButtonGroup>
                        <Button
                            onClick={() => {
                                gtagEvent('confirm_back_click');
                                onNavigate('schedule');
                            }}
                            disabled={isConfirming}
                            data-testid="back-btn"
                        >
                            Voltar
                        </Button>
                        <Button primary onClick={onConfirmBooking} disabled={isConfirming} data-testid="confirm-btn">
                            {isConfirming ? 'Confirmando...' : 'Confirmar'}
                        </Button>
                    </ButtonGroup>
                </>
            );
        }

        case 'intake':
            return (
                <IntakeSection
                    bookingService={bookingService}
                    businessSlug={businessSlug}
                    bookingId={lastBookingId!}
                    intakes={wizardIntakes.map((i) => ({
                        ...i,
                        status: 'pending' as const,
                        answers: [],
                    }))}
                    onViewBookings={() => {}}
                    onComplete={() => onNavigate('success')}
                    onSkip={() => onNavigate('success')}
                />
            );

        case 'success': {
            const successDescription = paymentInvoiceUrl
                ? 'Realize o pagamento para confirmar seu horário.'
                : 'Seu horário está reservado.';
            return (
                <div style={{ textAlign: 'center', paddingTop: 32 }}>
                    <SuccessIcon>✓</SuccessIcon>
                    <h2 style={{ marginBottom: 8 }} data-testid="screen-success">
                        {paymentInvoiceUrl ? 'Agendamento criado!' : 'Agendamento confirmado!'}
                    </h2>
                    <p>{successDescription}</p>
                    {paymentInvoiceUrl && (
                        <ButtonGroup style={{ justifyContent: 'center', marginTop: 24 }}>
                            <Button
                                primary
                                onClick={() => {
                                    gtagEvent('success_payment_click');
                                    window.open(paymentInvoiceUrl, '_blank');
                                }}
                            >
                                Visualizar cobrança
                            </Button>
                        </ButtonGroup>
                    )}
                    {reminders.enabled && (
                        <NotificationHint style={{ color: vars.accentColor, marginTop: 24 }}>
                            ✓ Lembretes ativados!
                        </NotificationHint>
                    )}
                    <ButtonGroup style={{ justifyContent: 'center', marginTop: reminders.enabled ? 8 : 24 }}>
                        <Button
                            primary={!paymentInvoiceUrl}
                            onClick={() => {
                                gtagEvent('success_bookings_click');
                                onViewBookings();
                            }}
                            data-testid="view-bookings-btn"
                        >
                            Ver meus agendamentos
                        </Button>
                    </ButtonGroup>
                </div>
            );
        }
    }
}
